import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Calendar, ScanLine, FileText } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function PatientAdvisory() {
  const [tab, setTab] = useState("patients");
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pOpen, setPOpen] = useState(false);
  const [sOpen, setSOpen] = useState(false);
  const [selSession, setSelSession] = useState(null);
  const [decision, setDecision] = useState({ decision: "", rationale: "", linked_dhf_section: "" });

  const [pForm, setPForm] = useState({ name: "", role: "Patient", demographic: "", condition: "", consent_signed: false });
  const [sForm, setSForm] = useState({ title: "", project_id: "", facilitator: "", participant_ids: [], session_date: "", location: "Virtual", agenda: "" });

  const loadAll = async () => {
    const [p, s, d] = await Promise.all([api.get("/patients"), api.get("/codesign-sessions"), api.get("/devices")]);
    setPatients(p.data); setSessions(s.data); setProjects(d.data);
  };
  useEffect(() => { loadAll(); }, []);

  const createPatient = async () => {
    if (!pForm.name) return toast.error("Name required");
    await api.post("/patients", pForm);
    toast.success("Advisor added");
    setPOpen(false);
    setPForm({ name: "", role: "Patient", demographic: "", condition: "", consent_signed: false });
    loadAll();
  };

  const createSession = async () => {
    if (!sForm.title || !sForm.session_date) return toast.error("Title and date required");
    await api.post("/codesign-sessions", { ...sForm, project_id: sForm.project_id || null });
    toast.success("Session scheduled");
    setSOpen(false);
    setSForm({ title: "", project_id: "", facilitator: "", participant_ids: [], session_date: "", location: "Virtual", agenda: "" });
    loadAll();
  };

  const openSession = async (id) => {
    const { data } = await api.get(`/codesign-sessions/${id}`);
    setSelSession(data);
  };

  const addDecision = async () => {
    if (!decision.decision || !decision.rationale) return toast.error("Decision + rationale required");
    await api.post(`/codesign-sessions/${selSession.session_id}/decisions`, decision);
    toast.success("Decision logged");
    setDecision({ decision: "", rationale: "", linked_dhf_section: "" });
    openSession(selSession.session_id);
  };

  const toggleParticipant = (id) => {
    setSForm({ ...sForm, participant_ids: sForm.participant_ids.includes(id)
      ? sForm.participant_ids.filter((x) => x !== id) : [...sForm.participant_ids, id] });
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="vq-overline mb-2">Person-Centred Compliance</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">Patient Advisory & Co-Design</h1>
        <p className="text-slate-600 mb-8 max-w-3xl">
          Patient representative directory + co-design session records with OCR-captured board photos and decisions linked to your DHF.
        </p>

        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[{ v: "patients", label: "Advisors" }, { v: "sessions", label: "Co-Design Sessions" }].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`pa-tab-${t.v}`}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === t.v ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "patients" && (
          <>
            <div className="flex justify-end mb-4">
              <Button onClick={() => setPOpen(true)} data-testid="pa-patient-new" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                <Plus className="w-4 h-4 mr-2" /> Add Advisor
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {patients.map((p) => (
                <div key={p.patient_id} className="vq-card p-5" data-testid={`pa-patient-${p.patient_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs font-mono text-slate-500">{p.role}</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1 text-xs text-slate-600">
                    {p.condition && <div><span className="text-slate-400 font-mono uppercase tracking-wider">Condition:</span> {p.condition}</div>}
                    {p.demographic && <div><span className="text-slate-400 font-mono uppercase tracking-wider">Demographic:</span> {p.demographic}</div>}
                    <div className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${p.consent_signed ? "bg-emerald-500" : "bg-amber-500"}`} />
                      {p.consent_signed ? "Consent signed" : "Consent pending"}
                    </div>
                  </div>
                </div>
              ))}
              {patients.length === 0 && <div className="col-span-full text-center py-12 text-slate-500">No advisors yet. Add your first patient / caregiver representative.</div>}
            </div>
          </>
        )}

        {tab === "sessions" && (
          <>
            <div className="flex justify-end mb-4">
              <Button onClick={() => setSOpen(true)} data-testid="pa-session-new" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                <Plus className="w-4 h-4 mr-2" /> New Session
              </Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 space-y-2">
                {sessions.map((s) => (
                  <button key={s.session_id} data-testid={`pa-session-${s.session_id}`}
                          onClick={() => openSession(s.session_id)}
                          className={`w-full text-left vq-card p-4 vq-card-hover ${selSession?.session_id === s.session_id ? "border-slate-900" : ""}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[10px] font-mono text-slate-500">{s.session_date}</span>
                    </div>
                    <div className="font-semibold text-sm text-slate-900">{s.title}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-1">
                      {(s.participant_ids || []).length} participants • {(s.decisions || []).length} decisions
                    </div>
                  </button>
                ))}
                {sessions.length === 0 && <div className="vq-card p-8 text-center text-slate-500 text-sm">No sessions yet.</div>}
              </div>

              <div className="lg:col-span-3">
                {selSession ? (
                  <>
                    <div className="vq-card p-6 mb-4">
                      <div className="vq-overline mb-1 font-mono">{selSession.session_id}</div>
                      <h2 className="font-display font-bold text-xl text-slate-900">{selSession.title}</h2>
                      <div className="text-xs font-mono text-slate-500 mt-1">{selSession.session_date} • {selSession.location} • facilitated by {selSession.facilitator}</div>
                      <p className="text-sm text-slate-700 mt-3">{selSession.agenda}</p>
                      <div className="mt-4">
                        <Link to="/ocr" data-testid="pa-session-ocr-link">
                          <Button variant="outline" className="border-slate-300 font-semibold">
                            <ScanLine className="w-4 h-4 mr-2" /> Capture board photos (OCR)
                          </Button>
                        </Link>
                      </div>
                    </div>

                    <div className="vq-card p-6">
                      <h3 className="font-display font-bold text-lg text-slate-900 mb-4">
                        Decisions Log ({(selSession.decisions || []).length})
                      </h3>
                      <div className="space-y-3 mb-6">
                        {(selSession.decisions || []).map((d) => (
                          <div key={d.decision_id} className="border-l-2 border-[#0055FF] pl-4 py-2">
                            <div className="text-sm font-semibold text-slate-900">{d.decision}</div>
                            <div className="text-xs text-slate-600 mt-1 italic">Rationale: {d.rationale}</div>
                            {d.linked_dhf_section && <div className="text-[10px] font-mono text-emerald-700 mt-1"><FileText className="w-3 h-3 inline mr-1" />DHF: {d.linked_dhf_section}</div>}
                            <div className="text-[10px] font-mono text-slate-400 mt-1">{d.recorded_by} • {new Date(d.recorded_at).toLocaleString()}</div>
                          </div>
                        ))}
                        {(selSession.decisions || []).length === 0 && <div className="text-sm text-slate-500">No decisions logged yet.</div>}
                      </div>
                      <div className="space-y-2 pt-4 border-t border-slate-200">
                        <Input data-testid="pa-decision-text" placeholder="Decision" value={decision.decision} onChange={(e) => setDecision({ ...decision, decision: e.target.value })} />
                        <Textarea data-testid="pa-decision-rationale" placeholder="Rationale" value={decision.rationale} onChange={(e) => setDecision({ ...decision, rationale: e.target.value })} rows={2} />
                        <Input data-testid="pa-decision-dhf" placeholder="Linked DHF section (optional)" value={decision.linked_dhf_section} onChange={(e) => setDecision({ ...decision, linked_dhf_section: e.target.value })} />
                        <Button onClick={addDecision} data-testid="pa-decision-add" className="bg-slate-900 hover:bg-[#0055FF] text-white">Log decision</Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="vq-card p-10 text-center text-slate-500">Select a session to view details.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Patient dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add Advisor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Name / Pseudonym</Label>
              <Input data-testid="pa-patient-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Role</Label>
              <Select value={pForm.role} onValueChange={(v) => setPForm({ ...pForm, role: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["Patient", "Caregiver", "Clinician", "Patient Advocate"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Condition</Label>
              <Input value={pForm.condition} onChange={(e) => setPForm({ ...pForm, condition: e.target.value })} className="mt-1.5" placeholder="e.g. Type 2 Diabetes" /></div>
            <div><Label className="text-xs font-semibold">Demographic</Label>
              <Input value={pForm.demographic} onChange={(e) => setPForm({ ...pForm, demographic: e.target.value })} className="mt-1.5" placeholder="e.g. 45-65, urban India" /></div>
            <label className="flex items-center gap-2 text-sm mt-2">
              <Checkbox checked={pForm.consent_signed} onCheckedChange={(v) => setPForm({ ...pForm, consent_signed: !!v })} data-testid="pa-patient-consent" />
              Consent signed (advisor agreement on file)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPOpen(false)}>Cancel</Button>
            <Button onClick={createPatient} data-testid="pa-patient-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session dialog */}
      <Dialog open={sOpen} onOpenChange={setSOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New Co-Design Session</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Title</Label>
              <Input value={sForm.title} data-testid="pa-session-title" onChange={(e) => setSForm({ ...sForm, title: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Date</Label>
                <Input type="date" value={sForm.session_date} onChange={(e) => setSForm({ ...sForm, session_date: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold">Location</Label>
                <Input value={sForm.location} onChange={(e) => setSForm({ ...sForm, location: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-semibold">Facilitator</Label>
              <Input value={sForm.facilitator} onChange={(e) => setSForm({ ...sForm, facilitator: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Project (optional)</Label>
              <Select value={sForm.project_id || "none"} onValueChange={(v) => setSForm({ ...sForm, project_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {projects.map((p) => (<SelectItem key={p.project_id} value={p.project_id}>{p.device_name}</SelectItem>))}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Agenda</Label>
              <Textarea value={sForm.agenda} onChange={(e) => setSForm({ ...sForm, agenda: e.target.value })} rows={3} className="mt-1.5" /></div>
            {patients.length > 0 && (
              <div>
                <Label className="text-xs font-semibold">Participants</Label>
                <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded p-2">
                  {patients.map((p) => (
                    <label key={p.patient_id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={sForm.participant_ids.includes(p.patient_id)} onCheckedChange={() => toggleParticipant(p.patient_id)} />
                      {p.name} <span className="text-[10px] font-mono text-slate-500">({p.role})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSOpen(false)}>Cancel</Button>
            <Button onClick={createSession} data-testid="pa-session-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
